import { useState } from 'react'
import { Loader2, Copy, Check, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TacticalCard } from '@/components/ui/TacticalCard'
import { DeviceList } from './DeviceList'
import { LicenseManager } from './LicenseManager'
import type { UserLicense, DeviceLicense, MapRequest, EnrichedUser } from '@/hooks/useAdmin'
import { formatDate, formatDateTime } from '@/hooks/useAdmin'

function getInitials(name: string | null | undefined, email: string | null | undefined): string {
  if (name) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
  }
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return '?'
}

type Tab = 'licenses' | 'devices' | 'maps'

type UserDetailsPanelProps = {
  user: EnrichedUser
  licenses: UserLicense[]
  devices: DeviceLicense[]
  maps: MapRequest[]
  licensesLoading?: boolean
  devicesLoading?: boolean
  mapsLoading?: boolean
  onToggleAdmin: (userId: string, isAdmin: boolean) => void
  onToggleBetaTester: (userId: string, isBetaTester: boolean) => void
  onUpdateLicense: (licenseId: string, updates: Partial<UserLicense>) => void
  onCreateLicense: (license: {
    user_id: string
    license_tier: string
    total_devices: number
    annual_quota_km2: number
    org_id?: string | null
  }) => void
  onDeleteDevice: (deviceId: string) => void
  deleteDeviceError?: string | null
  onResetDeleteError?: () => void
  isTogglingAdmin?: boolean
  isTogglingBetaTester?: boolean
  isUpdatingLicense?: boolean
  isCreatingLicense?: boolean
  isRevokingDevice?: boolean
  isDeletingDevice?: boolean
}

export function UserDetailsPanel({
  user,
  licenses,
  devices,
  maps,
  licensesLoading,
  devicesLoading,
  mapsLoading,
  onToggleAdmin,
  onToggleBetaTester,
  onUpdateLicense,
  onCreateLicense,
  onDeleteDevice,
  deleteDeviceError,
  onResetDeleteError,
  isTogglingAdmin,
  isTogglingBetaTester,
  isUpdatingLicense,
  isCreatingLicense,
  isDeletingDevice,
}: UserDetailsPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>('licenses')

  // Calculate stats
  const activeDevices = devices.filter((d) => d.status === 'active').length
  const activeLicense = licenses.find((l) => l.status === 'active')
  const totalDeviceSlots = activeLicense?.total_devices || 0
  const completedMaps = maps.filter((m) => m.status === 'completed')
  const usedQuotaKm2 = completedMaps.reduce((sum, m) => sum + (m.area_km2 || 0), 0)
  const totalQuotaKm2 = activeLicense?.annual_quota_km2 || 0
  const isMvps = activeLicense?.license_tier === 'LIC-MVPS'

  const initials = getInitials(user.full_name, user.email)
  const [copiedEmail, setCopiedEmail] = useState(false)

  const handleCopyEmail = () => {
    if (user.email) {
      navigator.clipboard.writeText(user.email)
      setCopiedEmail(true)
      setTimeout(() => setCopiedEmail(false), 1500)
    }
  }

  return (
    <div className="space-y-6">
      {/* User Header with Avatar */}
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(99,215,6,0.1)]">
          <span className="text-sm font-medium text-primary">{initials}</span>
        </div>

        {/* Name and Email */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-medium text-foreground truncate tracking-tight">
              {user.full_name || <span className="text-muted-foreground/40 italic">No name</span>}
            </h2>
            {user.is_admin && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full shadow-[0_0_10px_rgba(99,215,6,0.1)]">
                <span className="w-1 h-1 rounded-full bg-primary" />
                Admin
              </span>
            )}
            {user.is_beta_tester && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-sky-300 bg-sky-500/10 px-2 py-0.5 rounded-full">
                <span className="w-1 h-1 rounded-full bg-sky-300" />
                Beta
              </span>
            )}
          </div>
          {user.email && (
            <p className="text-sm text-muted-foreground/70 mt-0.5 truncate">{user.email}</p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {user.email && (
            <>
              <button
                onClick={handleCopyEmail}
                className="p-2 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-white/[0.04] transition-all"
                title="Copy email"
              >
                {copiedEmail ? (
                  <Check className="h-4 w-4 text-primary" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
              <a
                href={`mailto:${user.email}`}
                className="p-2 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-white/[0.04] transition-all"
                title="Send email"
              >
                <Mail className="h-4 w-4" />
              </a>
            </>
          )}
          <button
            onClick={() => onToggleAdmin(user.id, !user.is_admin)}
            disabled={isTogglingAdmin}
            className={cn(
              'px-3 py-1.5 rounded-md text-xs font-medium transition-all disabled:opacity-50',
              user.is_admin
                ? 'text-destructive/80 hover:text-destructive hover:bg-destructive/10'
                : 'text-primary/80 hover:text-primary hover:bg-primary/10'
            )}
          >
            {isTogglingAdmin ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : user.is_admin ? (
              'Remove admin'
            ) : (
              'Make admin'
            )}
          </button>
          <button
            onClick={() => onToggleBetaTester(user.id, !user.is_beta_tester)}
            disabled={isTogglingBetaTester}
            className={cn(
              'px-3 py-1.5 rounded-md text-xs font-medium transition-all disabled:opacity-50',
              user.is_beta_tester
                ? 'text-sky-300/80 hover:text-sky-200 hover:bg-sky-500/10'
                : 'text-primary/80 hover:text-primary hover:bg-primary/10'
            )}
          >
            {isTogglingBetaTester ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : user.is_beta_tester ? (
              'Revoke beta'
            ) : (
              'Grant beta'
            )}
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground/50">Devices</span>
          <p className="text-xl font-medium text-foreground mt-1.5 tabular-nums">
            {isMvps ? (
              <span className="text-muted-foreground/50 text-base">n/a</span>
            ) : (
              <>
                {activeDevices}
                <span className="text-muted-foreground/40 text-base">/{totalDeviceSlots}</span>
              </>
            )}
          </p>
        </div>
        <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground/50">Map Quota</span>
          <p className="text-xl font-medium text-foreground mt-1.5 tabular-nums">
            {isMvps ? (
              <span className="text-muted-foreground/50 text-base">Unlimited</span>
            ) : (
              <>
                {usedQuotaKm2.toFixed(1)}
                <span className="text-muted-foreground/40 text-base">/{totalQuotaKm2} km²</span>
              </>
            )}
          </p>
        </div>
        <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground/50">License</span>
          <p className="text-xl font-medium text-foreground mt-1.5">
            {activeLicense?.license_tier || <span className="text-muted-foreground/40">None</span>}
          </p>
        </div>
      </div>

      {/* User Metadata */}
      <div className="border border-white/[0.06] rounded-lg bg-white/[0.01] overflow-hidden">
        <MetadataRow label="Email" value={user.email || '-'} mono />
        <MetadataRow label="User UID" value={user.id} mono copyable />
        <MetadataRow label="Created at" value={formatDateTime(user.created_at ?? null)} />
        <MetadataRow label="Last signed in" value={formatDateTime(user.last_sign_in_at ?? null)} />
        <MetadataRow label="Beta tester" value={user.is_beta_tester ? 'Yes' : 'No'} />
        <MetadataRow label="Confirmed at" value={formatDateTime(user.email_confirmed_at ?? null)} last />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/[0.06]">
        <TabButton
          active={activeTab === 'licenses'}
          onClick={() => setActiveTab('licenses')}
          count={licenses.length}
        >
          Licenses
        </TabButton>
        <TabButton
          active={activeTab === 'devices'}
          onClick={() => setActiveTab('devices')}
          count={devices.length}
        >
          Devices
        </TabButton>
        <TabButton
          active={activeTab === 'maps'}
          onClick={() => setActiveTab('maps')}
          count={maps.length}
        >
          Maps
        </TabButton>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'licenses' && (
          <LicenseManager
            licenses={licenses}
            userId={user.id}
            isLoading={licensesLoading}
            onUpdateLicense={onUpdateLicense}
            onCreateLicense={onCreateLicense}
            isUpdating={isUpdatingLicense}
            isCreating={isCreatingLicense}
          />
        )}

        {activeTab === 'devices' && (
          <DeviceList
            devices={devices}
            isLoading={devicesLoading}
            onDeleteDevice={onDeleteDevice}
            deleteError={deleteDeviceError}
            onResetDeleteError={onResetDeleteError}
            isDeleting={isDeletingDevice}
          />
        )}

        {activeTab === 'maps' && (
          <MapsList maps={maps} isLoading={mapsLoading} />
        )}
      </div>
    </div>
  )
}

type TabButtonProps = {
  active: boolean
  onClick: () => void
  count: number
  children: React.ReactNode
}

function TabButton({ active, onClick, count, children }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-4 py-2.5 text-sm transition-all border-b-2 -mb-px',
        active
          ? 'text-foreground border-primary'
          : 'text-muted-foreground/60 border-transparent hover:text-foreground hover:bg-white/[0.02]'
      )}
    >
      {children}
      <span className={cn(
        'ml-1.5 text-xs',
        active ? 'text-muted-foreground/60' : 'text-muted-foreground/40'
      )}>
        {count}
      </span>
    </button>
  )
}

type MapsListProps = {
  maps: MapRequest[]
  isLoading?: boolean
}

function MapsList({ maps, isLoading }: MapsListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (maps.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-muted-foreground/70">No maps requested</p>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-primary/70'
      case 'processing':
        return 'text-blue-500/70'
      case 'failed':
        return 'text-destructive/70'
      case 'pending':
        return 'text-yellow-500/70'
      default:
        return 'text-muted-foreground/50'
    }
  }

  return (
    <div className="space-y-3">
      {maps.map((map) => (
        <TacticalCard key={map.id} variant="default" padding="default">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm text-foreground truncate">
                  {map.area_name || `Map #${map.id}`}
                </p>
                <span className={cn('text-xs', getStatusColor(map.status))}>
                  {map.status}
                </span>
              </div>
              <p className="text-xs text-muted-foreground/50 mt-1">
                {map.area_km2?.toFixed(2) || '—'} km² · {formatDate(map.created_at)}
              </p>
            </div>
          </div>
        </TacticalCard>
      ))}
    </div>
  )
}

type MetadataRowProps = {
  label: string
  value: string
  mono?: boolean
  copyable?: boolean
  last?: boolean
}

function MetadataRow({ label, value, mono, copyable, last }: MetadataRowProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between px-4 py-3 group',
        !last && 'border-b border-white/[0.04]'
      )}
    >
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground/50">{label}</span>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'text-sm text-foreground/90',
            mono && 'font-mono text-xs text-muted-foreground'
          )}
        >
          {value}
        </span>
        {copyable && value !== '-' && (
          <button
            onClick={handleCopy}
            className="p-1 rounded text-muted-foreground/30 hover:text-muted-foreground hover:bg-white/[0.04] opacity-0 group-hover:opacity-100 transition-all"
            title="Copy to clipboard"
          >
            {copied ? (
              <Check className="h-3 w-3 text-primary" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </button>
        )}
      </div>
    </div>
  )
}
